import React from 'react';
import {
  VStack, Text, HStack, Box, Divider, Pressable,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import SimpleRTE from '@/common/components/forms/SimpleRTE';
import { useTenantGradingSettings } from '@/common/context/TenantSettingsStore';
import { useUserFeatures } from '@/common/hooks/useUserFeatures';
import ContentGroup from '@/common/components/ContentGroup';
import type { ModuleMetadataWorkingMode } from '@/common/api/models/moduleMetadataWorkingMode';
import {
  getWorkingModeFieldLock,
} from '@/lecturer/ModuleEditor/workingMode/workingModeFieldLocks';
import type { Task } from '@/common/api/models/task';
import LevelForm from './LevelForm';
import ModuleImageForm from './ModuleImageForm';
import SegmentedToggle from './components/SegmentedToggle';
import DotLevelSelector from './components/DotLevelSelector';
import WorkingModeBlock from './components/WorkingModeBlock';

interface ModuleMetaFormProps {
  readonly?: boolean;
  autogradeIsReadOnly?: boolean;
  workingMode?: ModuleMetadataWorkingMode;
  moduleId?: string;
  version?: string;
  isDraft?: boolean;
  workflowState?: string;
  formIsDirty?: boolean;
  tasks?: Task[];
}

/**
 * Section header component for consistent styling
 */
const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text size="xs" fontWeight="bold" color="$textSubtle" textTransform="uppercase" letterSpacing="$md" mb="$2">
    {children}
  </Text>
);

const ModuleMetaForm: React.FC<ModuleMetaFormProps> = ({
  readonly = false,
  autogradeIsReadOnly = false,
  workingMode,
  moduleId,
  version,
  isDraft = true,
  workflowState,
  formIsDirty = false,
  tasks,
}) => {
  const { t } = useTranslation();
  const { evalDimensionKeys } = useTenantGradingSettings();
  const { isFeatureActive } = useUserFeatures();
  const autogradeAiEnabled = isFeatureActive('lecturer-task-ai');

  const { setValue } = useFormContext();
  const autogradeOnSubmission = useWatch({ name: 'autogradeOnSubmission' }) as boolean ?? false;
  const examinationBasis = useWatch({ name: 'examinationBasis' }) as 'task_submissions' | 'oral_exam' | null | undefined;

  const policyLock = getWorkingModeFieldLock({
    workingMode,
    field: 'module.taskCompletionPolicy',
  });
  const autogradeLock = getWorkingModeFieldLock({
    workingMode,
    field: 'module.autogradeOnSubmission',
  });
  const examinationBasisLock = getWorkingModeFieldLock({
    workingMode,
    field: 'module.examinationBasis',
  });
  const policyLocked = policyLock.locked;
  const autogradeLocked = autogradeLock.locked;
  const examinationBasisLocked = examinationBasisLock.locked;
  const autogradeReadOnly = autogradeIsReadOnly || autogradeLocked;
  const examinationBasisReadOnly = readonly || examinationBasisLocked;

  const formatFieldLockedHint = (modeLabelKey?: string) => (
    modeLabelKey
      ? t('lecturer.moduleEditor.workingMode.fieldLocked', {
        mode: t(modeLabelKey),
      })
      : undefined
  );

  return (
    <VStack space="lg">
      {moduleId && version && (
        <WorkingModeBlock
          moduleId={moduleId}
          version={version}
          persistedWorkingMode={workingMode}
          isDraft={isDraft}
          workflowState={workflowState}
          formIsDirty={formIsDirty}
          tasks={tasks}
        />
      )}
      {/* Row 1: Image + Description + Policies - three columns */}
      <HStack space="lg" alignItems="stretch">
        {/* Cover Image */}
        <Box w={300} flexShrink={0}>
          <SectionHeader>Cover Image</SectionHeader>
          <ModuleImageForm readonly={readonly} compact />
        </Box>

        {/* Description - plain text, no formatting toolbar */}
        <Box flex={1}>
          <SectionHeader>Description</SectionHeader>
          <SimpleRTE
            name="description"
            placeholder="Enter module description..."
            readonly={readonly}
            height={120}
            showAccentBar
            rules={{
              required: 'Module must have a description',
              validate: (value: string) => {
                const wordcount = (value || '').trim().split(/\s+/).length;
                if (wordcount < 20) {
                  return 'Module description must be longer than 20 words.';
                }
                if (wordcount > 100) {
                  return 'Module description must be shorter than 100 words.';
                }
                return true;
              },
            }}
          />
        </Box>

        {/* Completion Policies - using ContentGroup for consistency */}
        <Box w={240} flexShrink={0}>
          <SectionHeader>Policies</SectionHeader>
          <ContentGroup variant="accent" p="$3">
            <VStack space="md">
              {/* Progression mode toggle */}
              <VStack space="xs">
                <Text size="2xs" color="$textSubtle" fontWeight="bold" textTransform="uppercase" letterSpacing="$md">
                  PROGRESSION POLICY
                </Text>
                <SegmentedToggle
                  name="taskCompletionPolicy"
                  readonly={readonly || policyLocked}
                  options={[
                    { label: 'Free', value: 'free' },
                    { label: 'Gated', value: 'gated' },
                  ]}
                />
                {policyLocked && (
                  <Text size="2xs" color="$textHint">
                    {formatFieldLockedHint(policyLock.modeLabelKey)}
                  </Text>
                )}
              </VStack>

              {/* Min levels - always shown */}
              <VStack space="xs">
                <Text size="2xs" color="$textSubtle" fontWeight="bold" textTransform="uppercase" letterSpacing="$md">
                  MIN. LEVELS
                </Text>
                <VStack space="xs">
                  {evalDimensionKeys.map((dimKey) => (
                    <DotLevelSelector
                      key={`minLevel-${dimKey}`}
                      name={`taskCompletionMinLevels.${dimKey}`}
                      dimension={dimKey}
                      readonly={readonly}
                    />
                  ))}
                </VStack>
              </VStack>

              {/* Autograde on submission — gated by lecturer-task-ai */}
              {autogradeAiEnabled && (
                <VStack space="xs">
                  <Text size="2xs" color="$textSubtle" fontWeight="bold" textTransform="uppercase" letterSpacing="$md">
                    {t('lecturer.moduleEditor.moduleEditor.autogradeOnSubmission.label')}
                  </Text>
                  <HStack
                    testID="autograde-on-submission-toggle"
                    bg="$bgSubtle"
                    p={3}
                    borderRadius="$lg"
                    alignSelf="stretch"
                    borderWidth={1}
                    borderColor="$borderLight"
                  >
                    {([false, true] as const).map((val) => {
                      const isActive = autogradeOnSubmission === val;
                      return (
                        <Pressable
                          key={String(val)}
                          onPress={() => {
                            if (!autogradeReadOnly) {
                              setValue('autogradeOnSubmission', val, { shouldDirty: true });
                            }
                          }}
                          flex={1}
                          py="$1.5"
                          borderRadius="$md"
                          bg={isActive ? '$brandBase' : 'transparent'}
                          opacity={autogradeReadOnly ? 0.6 : 1}
                          alignItems="center"
                        >
                          <Text
                            size="xs"
                            color={isActive ? '$textInverted' : '$textSubtle'}
                            fontWeight="semibold"
                          >
                            {val
                              ? t('lecturer.moduleEditor.moduleEditor.autogradeOnSubmission.on')
                              : t('lecturer.moduleEditor.moduleEditor.autogradeOnSubmission.off')}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </HStack>
                  <Text size="2xs" color="$textHint">
                    {autogradeLocked
                      ? formatFieldLockedHint(autogradeLock.modeLabelKey)
                      : t('lecturer.moduleEditor.moduleEditor.autogradeOnSubmission.description')}
                  </Text>
                </VStack>
              )}

              <VStack space="xs">
                <Text size="2xs" color="$textSubtle" fontWeight="bold" textTransform="uppercase" letterSpacing="$md">
                  {t('lecturer.moduleEditor.moduleEditor.examinationBasis.label')}
                </Text>
                <HStack
                  testID="examination-basis-toggle"
                  bg="$bgSubtle"
                  p={3}
                  borderRadius="$lg"
                  alignSelf="stretch"
                  borderWidth={1}
                  borderColor="$borderLight"
                >
                  {(['task_submissions', 'oral_exam'] as const).map((val) => {
                    const isActive = examinationBasis === val;
                    return (
                      <Pressable
                        key={val}
                        onPress={() => {
                          if (!examinationBasisReadOnly) {
                            setValue('examinationBasis', val, { shouldDirty: true });
                          }
                        }}
                        flex={1}
                        py="$1.5"
                        borderRadius="$md"
                        bg={isActive ? '$brandBase' : 'transparent'}
                        opacity={examinationBasisReadOnly ? 0.6 : 1}
                        alignItems="center"
                      >
                        <Text
                          size="xs"
                          color={isActive ? '$textInverted' : '$textSubtle'}
                          fontWeight="semibold"
                        >
                          {val === 'task_submissions'
                            ? t('lecturer.moduleEditor.moduleEditor.examinationBasis.taskSubmissions')
                            : t('lecturer.moduleEditor.moduleEditor.examinationBasis.oralExam')}
                        </Text>
                      </Pressable>
                    );
                  })}
                </HStack>
                <Text size="2xs" color="$textHint">
                  {examinationBasisLocked
                    ? formatFieldLockedHint(examinationBasisLock.modeLabelKey)
                    : t('lecturer.moduleEditor.moduleEditor.examinationBasis.description')}
                </Text>
              </VStack>
            </VStack>
          </ContentGroup>
        </Box>
      </HStack>

      <Divider bg="$borderLight" />

      {/* Row 2: Level Structure - full width, 3 equal columns */}
      <VStack space="md">
        <SectionHeader>Level Structure</SectionHeader>
        <HStack space="md">
          {[1, 2, 3].map((level) => (
            <Box key={`levelForm-${level}`} flex={1}>
              <LevelForm level={level} readonly={readonly} />
            </Box>
          ))}
        </HStack>
      </VStack>
    </VStack>
  );
};

export default ModuleMetaForm;
